import { endOfDay, intervalToDuration } from 'date-fns'
import React from 'react'
import moment from 'moment'
import queryString from 'query-string'
import {
  isEmpty,
  isNil,
  isObject,
  isArray,
  startCase,
  isString,
  truncate,
  isNaN,
  toNumber,
  isFunction,
  isBoolean,
  isNumber,
  isSymbol,
  capitalize,
} from 'lodash'
import pluralize from 'pluralize'
import { sanitizeUrl } from '@braintree/sanitize-url'

import { customStaticRanges, getTriggerLabel } from '@/components/helpers'
import {
  dateFormatMoment,
  ENDING_SENTIMENT_RANGES,
  omnichannelTypeOptions,
  CHANNEL_TYPE,
} from '@/utils/constants'
import { CopyButton } from '@/components/buttons/CopyButton'
import { Pill } from '@/components/pills/Pill'
import { InteractiveTooltip } from '@/components/tooltips/InteractiveTooltip'
import { ANALYTICS_SECTIONS } from '@/views/Analytics/analytics.constants'

const { OVERVIEW, CHECKLIST, DYNAMIC_PROMPT, POSTCALL, USAGE, CSV } = ANALYTICS_SECTIONS

export const formatDurationPill = (minCallDuration, maxCallDuration) => {
  const noMinApplied = !Number(minCallDuration)
  const noMaxApplied = !Number(maxCallDuration)

  if (noMaxApplied && noMinApplied) {
    return 'All'
  }

  if (noMaxApplied) {
    return `${pluralize('minute', toNumber(minCallDuration), true)} or more`
  }

  if (noMinApplied) {
    return `${pluralize('minute', toNumber(maxCallDuration), true)} or less`
  }

  if (toNumber(minCallDuration) >= toNumber(maxCallDuration)) {
    return 'Invalid'
  }

  return `${toNumber(minCallDuration)} - ${pluralize('minute', toNumber(maxCallDuration), true)}`
}

export const formatDuration = (durationInSeconds = 0) => {
  const duration = intervalToDuration({
    start: 0,
    end: durationInSeconds * 1000,
  })
  const formattedHours = duration.hours > 9 ? `${duration.hours}` : `0${duration.hours}`
  const formattedMinutes = duration.minutes > 9 ? `${duration.minutes}` : `0${duration.minutes}`
  const formattedSeconds = duration.seconds > 9 ? `${duration.seconds}` : `0${duration.seconds}`

  return `${formattedHours}:${formattedMinutes}:${formattedSeconds}`
}

export const formatCallDuration = (callDuration) => {
  // All nil values should be "1"
  if (callDuration === '' || isNaN(callDuration) || isNil(callDuration)) {
    return '1'
  }

  // Half minutes are meaningful (0.5 = 30 seconds); anything finer is noise.
  // Non-finite and negative values can arrive via pasted input or crafted URLs
  const callDurationNumber = parseFloat(callDuration)
  if (Number.isFinite(callDurationNumber) && callDurationNumber >= 0) {
    return (Math.round(callDurationNumber * 2) / 2).toString()
  }

  return '1'
}

const getFormattedCategoryPaths = (categoryPaths = []) => {
  return categoryPaths.reduce((acc, categoryOption) => {
    const parts = categoryOption.split(' - ')
    if (parts.length < 3) return acc
    // discard uuid and section. Keep category category_entry (if present) and decklist_entry (if present)
    const keepParts = parts.slice(2)
    const mergedParts = keepParts.join('-')
    acc.push(mergedParts)
    return acc
  }, [])
}

const getFilterObject = (filter = {}, section) => {
  const startDate = filter.startDate ? moment(filter.startDate).startOf('day').format() : undefined
  const endDate = filter.endDate ? moment(filter.endDate).endOf('day').format() : undefined
  let categories = []

  if (section === DYNAMIC_PROMPT) {
    categories = getFormattedCategoryPaths(filter.deck.selected) || []
  } else if (section === POSTCALL) {
    categories = filter[`${section}Categories`]?.map(({ value }) => value) || []
  }

  // When a single org is selected via the hierarchy picker, use it as the effective
  // organization so that single-org endpoints (items_by_user, etc.) query the correct org
  const hierarchySections = [OVERVIEW, USAGE, CHECKLIST, DYNAMIC_PROMPT, POSTCALL]
  const singleHierarchyOrg =
    hierarchySections.includes(section) && filter.organizations?.length === 1
      ? filter.organizations[0]
      : null
  const effectiveOrganization = singleHierarchyOrg || filter.organization

  const filterObject = {
    organization: effectiveOrganization,
    organization_id: effectiveOrganization,
    ...(hierarchySections.includes(section) &&
      !isEmpty(filter.organizations) && { organization_ids: filter.organizations }),
    agents: filter.agents?.map(({ value }) => value),
    playbooks: filter.playbooks?.map(({ value }) => value),
    tags: filter.tags?.map(({ value, tag_cat }) => `${tag_cat}:${value}`),
    categories,
    call_duration: formatCallDuration(filter.callDuration),
    max_call_duration: !filter.maxCallDuration ? 0 : filter.maxCallDuration,
    include_managers: filter.includeManagers,
    start_date: startDate,
    end_date: endDate,
    sections: filter.sections,
    is_win: filter.isWin,
    dispositions: filter.dispositions?.map(({ value }) => value),
    voip_campaign_names: filter.voipCampaigns?.map(({ value }) => value),
    ...(section === USAGE && { include_deleted_users: filter.includeDeletedUsers }),
  }

  // new reporting service uses "organization_id" instead of "organization" once everything is moved deprecate "organization"
  // new reporting service does not tolerate nulls/empty string for these if not passed (not org 1 doesn't provide)
  if (!effectiveOrganization) {
    delete filterObject.organization
    delete filterObject.organization_id
  }

  if (isNil(filter.isWin) || section === USAGE || section === CSV) {
    delete filterObject.is_win
  }

  if (filter.voip_campaign_names === null || section === USAGE || section === CSV) {
    delete filterObject.voip_campaign_names
  }

  if (isEmpty(filter.dispositions || section === USAGE || section === CSV)) {
    delete filterObject.dispositions
  }

  return filterObject
}

export const getFilterQueryString = (filter = {}, section) => {
  const filterObject = getFilterObject(filter, section)
  return queryString.stringify(filterObject)
}

export const getFilterBodyString = (filter = {}, section) => {
  const filterObject = getFilterObject(filter, section)
  return JSON.stringify(filterObject)
}

export const getOrganizationOptions = (organizations = [], isTruncated = false) => {
  return organizations
    .map((organization) => ({
      value: organization.id,
      label: isTruncated ? truncate(organization.name, { length: 35 }) : organization.name,
    }))
    .sort((a, b) => a.label.localeCompare(b.label))
}

export const getHierarchyOrganizationOptions = (organizations = [], isTruncated = false) => {
  return organizations
    .map(({ name, organization_id }) => ({
      value: organization_id,
      label: isTruncated ? truncate(name, { length: 35 }) : name,
    }))
    .sort((a, b) => a.label.localeCompare(b.label))
}

export const getAgentOptions = (agents = []) => {
  return agents
    .map((agent) => ({
      value: agent.id,
      // Trimmed: an agent whose name is a single word stores a space in
      // `last_name` (the column is NOT NULL), so an untrimmed join leaves a
      // trailing space in every filter and export this feeds.
      label: `${agent.first_name || ''} ${agent.last_name || ''}`.trim(),
      isAi: agent.ai_agent,
    }))
    .sort((a, b) => a.label.localeCompare(b.label))
}

export const getPlaybookOptions = (playbooks = []) => {
  return playbooks
    .map((playbook) => ({
      value: playbook.cid,
      label: playbook.name,
    }))
    .sort((a, b) => a.label.localeCompare(b.label))
}

export const getScorecardOptions = (scorecards = []) => {
  return scorecards.map((scorecard) => ({
    value: scorecard.id,
    label: scorecard.name,
    sid: scorecard.sid, // Needed for aggregate endpoint
    callType: scorecard.call_type,
  }))
}

export const getPlaybookOptionsForScorecards = (playbooks = []) => {
  return playbooks.map((playbook) => ({
    value: playbook.value,
    label: playbook.label,
    cid: playbook.value,
  }))
}

export const getPlaylistsOptions = (playlists = []) => {
  return playlists.map((playlist) => ({
    value: playlist.id,
    label: playlist.name,
  }))
}

export const getDispositionOptions = (dispositions = []) => {
  return dispositions
    .map(({ name }) => ({ value: name, label: name }))
    .sort((a, b) => a.label.localeCompare(b.label))
}

export const getSectionOptions = (selectedScorecard) => {
  if (selectedScorecard) {
    return selectedScorecard.sections?.map((section) => ({
      value: section.id,
      label: section.name,
    }))
  }
  return []
}

export const getNestedOptions = (selectedOption, sectionName) => {
  const nestedOption = []
  if (selectedOption?.length > 0) {
    selectedOption.forEach((section) => {
      const nestedObject = {
        label: section.name,
        options: [],
      }
      const formatObject = section[sectionName]?.map((measure) => {
        return {
          value: measure.id,
          label: measure.name,
        }
      })
      nestedObject.options.push(...formatObject)
      nestedOption.push(nestedObject)
    })
  }
  return nestedOption
}

export const getTagOptions = (tags = []) => {
  return tags
    .map((tag) => ({
      label: tag.name,
      value: tag.id,
      tag_cat: tag.tag_category_id,
    }))
    .sort((a, b) => a.label.localeCompare(b.label))
}

export const getTagsWithCategories = (tags = [], tagCategories = []) => {
  const tagsWithCategories = []

  // Created grouped tag categories
  tagCategories.forEach((cat) => {
    tagsWithCategories.push({
      label: cat.name,
      options: getTagOptions(tags.filter((tag) => tag.tag_category_id === cat.id)),
    })
  })

  // Add uncategorized tags
  tagsWithCategories.push({
    label: 'Uncategorized',
    options: getTagOptions(tags.filter((tag) => !tag.tag_category_id)),
  })

  return tagsWithCategories
}

export const getOrganizationsForBaltoOrHierarchy = ({
  organizations = [],
  flattenedUserOrgHierarchy = [],
  isBaltoAdmin = false,
  isHierarchyManager = false,
  childOrgSelected = false,
}) => {
  const showManagerHierarchyOrgDropdown = !isBaltoAdmin && isHierarchyManager && !childOrgSelected
  const hasOrganizationDropdown = isBaltoAdmin || showManagerHierarchyOrgDropdown
  const organizationOptions = getOrganizationOptions(organizations)
  const hierarchyOrganizationOptions = getHierarchyOrganizationOptions(flattenedUserOrgHierarchy)
  const organizationOptionsToUse = showManagerHierarchyOrgDropdown
    ? hierarchyOrganizationOptions
    : organizationOptions

  return { hasOrganizationDropdown, organizationOptions: organizationOptionsToUse }
}

// None selected is treated as multiple; only meaningful when the org hierarchy selector is
// available — the flattened hierarchy includes the user's own org, so length > 1 means the
// org actually has children
export const getMultipleOrgsSelected = ({
  isBaltoAdmin,
  isHierarchyManager,
  flattenedUserOrgHierarchy,
  organizations,
}) =>
  !isBaltoAdmin &&
  isHierarchyManager &&
  flattenedUserOrgHierarchy.length > 1 &&
  organizations?.length !== 1

export const getDateFormat = (date, groupByValue) => {
  switch (groupByValue) {
    case 'week':
      return `${moment.utc(date).format('M/DD')} - ${moment
        .utc(date)
        .add(6, 'days')
        .format('M/DD')}`
    case 'month':
      return moment.utc(date).format('MMMM')
    default:
      return moment.utc(date).format('M/DD')
  }
}

export function getPercentNumerical(numerator, denominator) {
  if (denominator > 0) {
    return Math.round(Math.abs((numerator / denominator) * 100))
  }

  return 0
}

export const formatPercent = (value, decimals, lessThanOne = false) => {
  if (isNaN(value) || value === 0) {
    return '0%'
  }

  if (decimals) {
    if (value < 1 && lessThanOne) {
      return '< 1%'
    }
    return `${parseFloat(value).toFixed(decimals)}%`
  }

  return `${Math.round(Math.abs(value))}%`
}

export const formatSeconds = (seconds) => {
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds - mins * 60)

  return `${mins}m ${secs}s`
}

export const getChecklistPercent = (checklistData) => {
  if (checklistData.length) {
    const checklistTotals = checklistData.reduce((a, b) => {
      // eslint-disable-next-line no-return-assign, no-param-reassign
      Object.keys(b).map((c) => (a[c] = (a[c] || 0) + b[c]))

      return a
    }, {})

    if (Object.keys(checklistTotals).length > 0 && checklistTotals.total > 0) {
      return getPercentNumerical(checklistTotals.items, checklistTotals.total)
    }
  }

  return 0
}

export function explodeItemsByUser(items = [], users = []) {
  const agents = users.reduce((acc, item) => {
    const totalCalls = item?.total_calls || 0
    const totalUsedEvents = item?.total_used_events || 0
    const totalPossibleEvents = item?.total_possible_events || 0

    acc[item.id] = {
      first_name: item?.first_name,
      last_name: item?.last_name,
      ...(totalCalls !== 0 && {
        total_calls_count: totalCalls,
      }),
      ...(totalCalls !== 0 && {
        average_completion_percent: getPercentNumerical(totalUsedEvents, totalPossibleEvents),
      }),
    }
    return acc
  }, {})

  return items.flatMap((item) =>
    item.users.map((userEntry) => ({
      item: item.item,
      count: userEntry?.num_calls_with_event || 0,
      total: userEntry?.total_calls || 0,
      user: agents[userEntry.id],
    }))
  )
}

export function explodeItemsByOrganization(items = [], organizations = []) {
  const orgsLookup = organizations.reduce((acc, org) => {
    const totalCalls = org?.total_calls || 0
    const totalUsedEvents = org?.total_used_events || 0
    const totalPossibleEvents = org?.total_possible_events || 0

    acc[org.id] = {
      organization_id: org.id,
      organization_name: org.name,
      ...(totalCalls !== 0 && {
        total_calls_count: totalCalls,
      }),
      ...(totalCalls !== 0 && {
        average_completion_percent: getPercentNumerical(totalUsedEvents, totalPossibleEvents),
      }),
    }
    return acc
  }, {})

  return items.flatMap((item) =>
    item.organizations.map((orgEntry) => ({
      item: item.item,
      count: orgEntry?.num_calls_with_event || 0,
      total: orgEntry?.total_calls || 0,
      organization: orgsLookup[orgEntry.id],
    }))
  )
}

// API returns items with both "category" and "item" fields. Different categories can have
// items with the same name. The UI treats "item" as the identifier, so we aggregate counts
// for items that share the same name across categories.

export function aggregateByItemName(usageByItem = []) {
  const aggregated = {}
  usageByItem.forEach((entry) => {
    const key = entry.item
    if (!aggregated[key]) {
      aggregated[key] = { item: entry.item, count: 0, total: 0, win_count: 0 }
    }
    aggregated[key].count += entry.count || 0
    aggregated[key].total += entry.total || 0
    aggregated[key].win_count += entry.win_count || 0
  })
  return Object.values(aggregated)
}

export function aggregateItemsByDate(itemsByDate = []) {
  const aggregated = {}
  itemsByDate.forEach((entry) => {
    const key = `${entry.item}|${entry.date}`
    if (!aggregated[key]) {
      aggregated[key] = { item: entry.item, date: entry.date, count: 0, total: 0, win_count: 0 }
    }
    aggregated[key].count += entry.count || 0
    aggregated[key].total += entry.total || 0
    aggregated[key].win_count += entry.win_count || 0
  })
  return Object.values(aggregated)
}

export function aggregateItemsByUser(itemsByUser = []) {
  const aggregated = {}
  itemsByUser.forEach((entry) => {
    // Defensive check: user can be undefined if user ID isn't found in agents map
    const userKey = entry.user ? `${entry.user.first_name}|${entry.user.last_name}` : 'unknown'
    const key = `${entry.item}|${userKey}`
    if (!aggregated[key]) {
      aggregated[key] = { item: entry.item, count: 0, total: 0, user: entry.user }
    }
    aggregated[key].count += entry.count || 0
    aggregated[key].total += entry.total || 0
  })
  return Object.values(aggregated)
}

// Removes duplicate item names from the items array. Needed because widgets use this array
// to build legends and chart series - duplicates would create duplicate UI entries.
export function deduplicateItems(items = []) {
  return [...new Set(items)]
}

export function truncateString(string, num) {
  if (string.length > num) {
    // eslint-disable-next-line prefer-template
    return string.slice(0, num - 3) + '...'
  }
  return string
}

export const truncateAgentName = (agentName) => {
  if (agentName) {
    const agentFirstInitial = agentName.split(' ')[0].charAt(0)
    return `${agentFirstInitial}. ${agentName.split(' ')[1]}`
  }
  return false
}

export const compareToPreviousRange = (currentData, prevData) => {
  if (!prevData) {
    return null
  }

  if (currentData === prevData) {
    return 0
  }

  const result = (Math.abs(currentData - prevData) / prevData) * 100
  const isDecimal = Math.ceil(result) !== result

  return isDecimal ? result.toFixed(2) : result
}

export const findPreviousDateRangeFromCurrentFilters = (startDate, endDate) => {
  if (!startDate || !endDate) {
    return [startDate, endDate]
  }

  const parsedStartDate = new Date(startDate)
  const parsedEndDate = new Date(endDate)
  const numDays = parsedEndDate - parsedStartDate || 86400000 // number of milliseconds in a day, because default is one day
  const newStartDate = new Date(parsedStartDate - numDays)
  const newEndDate = new Date(parsedEndDate - numDays)

  return [newStartDate, newEndDate]
}

export const getCardVariant = (currentData, prevData) => {
  if (currentData > prevData) {
    return 'positive'
  }

  if (currentData < prevData) {
    return 'negative'
  }

  return 'neutral'
}

export const getCardVariantReverse = (currentData, prevData) => {
  if (currentData < prevData) {
    return 'positive'
  }

  if (currentData > prevData) {
    return 'negative'
  }

  return 'neutral'
}

const getAvg = (arr) => {
  const total = arr.reduce((acc, c) => acc + c, 0)
  return total / arr.length
}

const getSum = (arr) => {
  return arr.reduce((acc, c) => acc + c, 0)
}
// https://github.com/heofs/trendline stolen from this
// data science confirmed math is good
export const createTrend = (data, xKey, yKey) => {
  const xData = data.map((value) => value[xKey])
  const yData = data.map((value) => value[yKey])

  const xMean = getAvg(xData)
  const yMean = getAvg(yData)

  const xMinusxMean = xData.map((val) => val - xMean)
  const yMinusyMean = yData.map((val) => val - yMean)

  const xMinusxMeanSq = xMinusxMean.map((val) => val ** 2)

  const xy = []
  for (let x = 0; x < data.length; x++) {
    xy.push(xMinusxMean[x] * yMinusyMean[x])
  }

  const xySum = getSum(xy)

  const slope = xySum / getSum(xMinusxMeanSq)
  const yStart = yMean - slope * xMean

  return {
    slope,
    yStart,
    calcY: (xValue) => yStart + slope * xValue,
  }
}

export const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list)
  const [removed] = result.splice(startIndex, 1)
  result.splice(endIndex, 0, removed)

  return result
}

export const formatTime = (time, formatString = dateFormatMoment.long) => {
  return moment.utc(time).local().format(formatString)
}

export const paramPusher = (filterValues, params, data, key, property = 'value') => {
  if (!isEmpty(filterValues[data])) {
    filterValues[data].forEach((item) => params.append(key, item[property]))
  }
}

export const cleanBooleanValue = (value) => {
  if (value === true || value === 'true') {
    return true
  }

  if (value === false || value === 'false') {
    return false
  }

  return null
}

export const sharedScorecardParams = (
  filterTypes,
  filterValues,
  params,
  orgId,
  isCopilot = false
) => {
  if (typeof orgId === 'number') {
    params.set('org_id', orgId)
    params.set('requested_organization_id', orgId)
  }

  // Some endpoints set scorecard_type by default, so only set if not already present
  if (!params.has('scorecard_type')) {
    if (isCopilot) {
      params.set('scorecard_type', 'copilot')
    } else {
      params.set('scorecard_type', 'automated')
    }
  }

  if (filterValues.taskTypes) {
    if (typeof filterValues.taskTypes === 'string') {
      params.set('task_type', filterValues.taskTypes)
    } else {
      filterValues.taskTypes.forEach((filterValue) => params.append('task_type', filterValue))
    }
  }

  if (filterValues.scorecardType) {
    if (typeof filterValues.scorecardType === 'string')
      params.set('scorecard_type', filterValues.scorecardType)
    else {
      filterValues.scorecardType.forEach((filterValue) =>
        params.append('scorecard_type', filterValue)
      )
    }
  }

  const cleanedIsWin = cleanBooleanValue(filterValues.isWin)
  if (cleanedIsWin !== null) {
    params.set('is_win', cleanedIsWin)
  }

  if (!isEmpty(filterValues.scoredStatus)) {
    params.set('scored_status', filterValues.scoredStatus)
  }

  if (filterValues.startDate) {
    params.set('start_date', moment(filterValues.startDate).utc().format())
  }

  if (filterValues.endDate) {
    params.set('end_date', moment(filterValues.endDate).utc().format())
  }
  if (filterValues.callType) {
    params.set('call_type', filterValues.callType)
  }
  if (filterValues.scorecardThreshold) {
    params.set('threshold', filterValues.scorecardThreshold)
  }
  filterTypes.forEach((filter) =>
    paramPusher(filterValues, params, filter.data, filter.key, filter.value)
  )
}

export const getPlaceholderContent = (data, loading, type) => {
  if (loading[type]) {
    return { placeholder: 'Loading...' }
  }

  if (!isEmpty(data[type])) {
    return { placeholderPill: `All ${startCase(pluralize(type))}` }
  }

  return { placeholder: 'None available' }
}

export const getRangeInDays = (startDate, endDate) => {
  const duration = moment.duration(moment(endDate).diff(moment(startDate)))
  const days = Math.floor(duration.asDays())

  return days
}

export const getTickValuesForDateRange = (days) => {
  if (days < 7) {
    return 'every day'
  }

  if (days < 31) {
    return 'every 5 days'
  }

  if (days < 61) {
    return 'every week'
  }

  return 'every week'
}

export const convertAgentNameToInitials = (agentName) => {
  return agentName
    .match(/(\b\S)?/g)
    .join('')
    .match(/(^\S|\S$)?/g)
    .join('')
    .toUpperCase()
}

export const formatTimeForAudio = (secs) => {
  const minutes = Math.floor(secs / 60)
  const seconds = Math.floor(secs % 60)
  const returnedSeconds = seconds < 10 ? `0${seconds}` : `${seconds}`

  return `${minutes}:${returnedSeconds}`
}

export const audioErrorSwitch = (errorCode, errorMessage) => {
  console.error(errorMessage)

  // https://html.spec.whatwg.org/multipage/media.html#dom-media-error-dev
  switch (errorCode) {
    case 1:
      console.error('Audio loading aborted by user')
      break
    case 2:
      console.error('Audio loading interrupted by a network error')
      break
    case 3:
      console.error(
        'Audio loading interrupted during decoding. The audio was determined to be unusable'
      )
      break
    case 4:
      console.error(
        "The audio URL is not valid. Either this call is too new and a file doesn't exist, or there is a problem with existing the audio file."
      )
      break
    default:
      console.error('Unknown audio player error, please refresh your page.')
      break
  }
}

export const showFirstResultDispositions = (arr = [], defaultValue = '0 selected', type = '') => {
  if (!Array.isArray(arr) || arr.length === 0) {
    return defaultValue
  }

  if (type === 'dispositions' && arr.length === 1) {
    const item = arr[0]
    return item.value || item.label || defaultValue
  }

  if (arr.length === 1) {
    return arr[0].label
  }

  return `${arr.length} selected`
}

export const showOrganizationResult = (value, organizationOptions = []) => {
  const org = organizationOptions.find((org) => org.value === value)

  if (org) {
    return truncate(org.label, { length: 25 })
  }

  return null
}

export const showDate = ({ startDate, endDate }) => {
  if (startDate && endDate) {
    return getTriggerLabel(startDate, endDate, true, true)
  }
  return null
}

export const showResult = (arr = [], defaultValue = null, showAll = false) => {
  if (arr.length === 1) {
    return truncate(arr[0].label, { length: 25 })
  }

  if (showAll && arr.length > 1) {
    return arr.map((item) => item.label && truncate(item.label, { length: 25 })).join(', ')
  }

  if (!isEmpty(arr)) {
    return (
      <Pill xs circular>
        {arr.length}
      </Pill>
    )
  }

  return defaultValue
}

export const showNestedResult = (arr = []) =>
  !isEmpty(arr) ? (
    <Pill xs circular>
      {arr.length}
    </Pill>
  ) : (
    'N/A'
  )

export const getUnixMs = (date) => new Date(date).valueOf() // Convert date to Unix milliseconds

export const markdownLinkRenderer = {
  link(rawHref, text) {
    // marked-react increments elementId before invoking custom renderers, so it's a
    // stable, unique key within the parse; the prefix keeps it from colliding with
    // the default renderer's marked-react-* keys.
    const key = `marked-link-${this?.elementId}`
    // Markdown is model-generated and can carry attacker-influenced text (e.g. an
    // approval summary); sanitizeUrl neutralizes javascript:/data:/etc. hrefs.
    const href = sanitizeUrl(rawHref)
    const isCall = href.includes('calls')
    const isMessage = href.includes('messages')
    if (isCall || isMessage) {
      const id = href.split('/').pop()

      return (
        <span key={key} className="inline-flex flex-align-center small-gap">
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="blurple-link"
            data-testid={`external-link-${id}`}
          >
            {text}
          </a>
          <CopyButton iconOnly basic showTooltip content={id} data-testid={`${id}-copy`} />
        </span>
      )
    }

    return (
      <a key={key} href={href} data-testid="link-no-copy" target="_blank" rel="noopener noreferrer">
        {text}
      </a>
    )
  },
}

export const getDateRangeFromSavedFilter = (dateRangeKey) => {
  let startDate = null
  let endDate = null

  const staticRange = customStaticRanges.find(
    (staticRange) => staticRange.label.toLowerCase() === dateRangeKey
  )

  if (staticRange) {
    const { startDate: start, endDate: end } = staticRange.range()
    startDate = start
    // This Week's preset ends at midnight today, and these dates are searched as they are, so today's calls would drop.
    endDate = endOfDay(end)
  } else {
    startDate = moment().startOf('day').utc().format()
    endDate = moment().endOf('day').utc().format()
  }

  return { startDate, endDate }
}

export const hasContent = (content) => {
  if (isArray(content) || isObject(content)) {
    return !isEmpty(content)
  }

  if (isString(content)) {
    return !isEmpty(content.trim())
  }

  if (isNumber(content) || isBoolean(content)) {
    return true
  }

  return false
}

export const formatArrayContent = (arrayContent = []) => (
  <ul className="summary-list-content">
    {arrayContent.filter(hasContent).map((item) => {
      const itemToRender = isString(item) ? item : JSON.stringify(item)

      return <li key={`list-item-${itemToRender}`}>{itemToRender}</li>
    })}
  </ul>
)

export const formatNestedContent = (nestedContent) => (
  <ul className="summary-list-content">
    {Object.keys(nestedContent).map((key) => {
      const keyToRender = isString(key) ? key : JSON.stringify(key)
      const itemToRender = isString(nestedContent[key])
        ? nestedContent[key]
        : JSON.stringify(nestedContent[key])

      return (
        <li key={`list-item-${keyToRender}`}>{`${startCase(keyToRender)}: ${itemToRender}`}</li>
      )
    })}
  </ul>
)

// An edited summary comes back from the API as plain text, so a field that was
// originally a JSON list arrives as newline-separated dashes ("- First\n- Second").
// Bullet lines render as a list again so edited and original views match; any other
// multiline string keeps its newlines instead of letting HTML collapse them.
// Line endings depend on whatever produced the edited text, so CRLF and a lone CR
// count as breaks too rather than leaving a stray \r inside a rendered item.
const LINE_BREAK = /\r\n|\r|\n/
const CARRIAGE_RETURNS = /\r\n|\r/g
const BULLET_LINE_PREFIX = /^\s*[-*\u2022]\s+/

export const hasLineBreak = (content) => LINE_BREAK.test(content)

export const formatMultilineContent = (content) => {
  const lines = content.split(LINE_BREAK).filter(hasContent)

  if (!isEmpty(lines) && lines.every((line) => BULLET_LINE_PREFIX.test(line))) {
    return formatArrayContent(lines.map((line) => line.replace(BULLET_LINE_PREFIX, '').trimEnd()))
  }

  // Normalized so the span renders and copies the same way regardless of the
  // line endings the text arrived with.
  return <span className="preserve-newlines">{content.replace(CARRIAGE_RETURNS, '\n')}</span>
}

// The same list however it arrived: a real array, a JSON array that came back as
// a string, or lines marked with -, * or •. Returns null when the value isn't a
// list at all, so callers can tell "not a list" from "an empty one".
//
// One definition of what counts as a list, so a value renders and compares the
// same way whichever shape the model happened to answer in.
export const toListItems = (content) => {
  const toItems = (items) =>
    items.filter(hasContent).map((item) => (isString(item) ? item.trim() : JSON.stringify(item)))

  if (isArray(content)) {
    return toItems(content)
  }

  if (!isString(content)) {
    return null
  }

  const trimmed = content.trim()
  if (trimmed.startsWith('[')) {
    try {
      const parsed = JSON.parse(trimmed)
      if (isArray(parsed)) {
        return toItems(parsed)
      }
    } catch (error) {
      // Not JSON after all — it may still be a bulleted list.
    }
  }

  const lines = trimmed.split(LINE_BREAK).filter(hasContent)
  if (isEmpty(lines) || !lines.every((line) => BULLET_LINE_PREFIX.test(line))) {
    return null
  }

  return lines.map((line) => line.replace(BULLET_LINE_PREFIX, '').trimEnd())
}

export const formatContent = (content) => {
  // isBoolean needs to happen before isEmpty since bools count as "empty"
  if (isBoolean(content)) {
    return capitalize(String(content))
  }

  // isNumber needs to happen before isEmpty since 0 counts as "empty"
  if (isNumber(content)) {
    return String(content)
  }

  if (isEmpty(content)) {
    return ''
  }

  if (isArray(content)) {
    return formatArrayContent(content)
  }

  if (isObject(content)) {
    return formatNestedContent(content)
  }

  if (isString(content) && hasLineBreak(content)) {
    return formatMultilineContent(content)
  }

  return content
}

export const getSummaryValue = (summary, { maxDepth = 10, _currentDepth = 0 } = {}) => {
  if (isNil(summary)) {
    return ''
  }

  // Symbols throw on string interpolation (e.g. a React element's $$typeof)
  if (isSymbol(summary)) {
    return ''
  }

  const nextOpts = { maxDepth, _currentDepth: _currentDepth + 1 }

  if (isArray(summary)) {
    if (_currentDepth >= maxDepth) {
      return ''
    }
    return summary.map((item) => `- ${getSummaryValue(item, nextOpts)}`).join('\n')
  }

  if (React.isValidElement(summary)) {
    if (_currentDepth >= maxDepth) {
      return ''
    }
    return getSummaryValue(summary.props.children, nextOpts)
  }

  if (isObject(summary)) {
    if (_currentDepth >= maxDepth) {
      return ''
    }
    return Object.keys(summary)
      .map((key) => `${startCase(key)}: ${getSummaryValue(summary[key], nextOpts)}`)
      .join('\n')
  }

  return summary
}

export const formatSummaryTableContent = (content) => {
  const hasValidContent = hasContent(content)

  if (!hasValidContent) {
    return ''
  }

  // Strings stay strings here so they keep the single-line truncate-and-tooltip
  // treatment a table cell needs, rather than picking up formatContent's
  // multiline rendering.
  const formattedContent =
    isObject(content) || isArray(content) || isString(content)
      ? getSummaryValue(content)
      : formatContent(content)

  if (isString(formattedContent)) {
    const fitColumnWidthToContentWidth = formattedContent.length < 60

    if (fitColumnWidthToContentWidth) {
      return <div>{formattedContent}</div>
    }

    return (
      <InteractiveTooltip interactive content={formattedContent} placement="top">
        <div className="table-summary-section">{formattedContent}</div>
      </InteractiveTooltip>
    )
  }

  return formattedContent
}

export const filterHierarchy = (node, query, { keepChildrenOnMatch = false } = {}) => {
  if (!node) {
    return null
  }

  const matches = node.name.toLowerCase().includes(query.toLowerCase())
  const filteredChildren = node.children
    ? node.children
        .map((child) => filterHierarchy(child, query, { keepChildrenOnMatch }))
        .filter(Boolean)
    : []

  if (matches || filteredChildren.length > 0) {
    return {
      ...node,
      children: matches && keepChildrenOnMatch ? node.children || [] : filteredChildren,
    }
  }

  return null
}

export const getStringValueFromBoolean = (value, defaultValue) => {
  if (value === true) {
    return 'true'
  }

  if (value === false) {
    return 'false'
  }

  return defaultValue
}

export const getBooleanValueFromString = (value, defaultValue) => {
  if (value === 'true') {
    return true
  }

  if (value === 'false') {
    return false
  }

  return defaultValue
}

export const getSearchSchemaForBoolean = (
  label,
  trueLabel = 'Enabled',
  falseLabel = 'Not Enabled'
) => {
  return {
    searchSchema: {
      label,
      type: 'select',
      options: [
        { label: trueLabel, value: true },
        { label: falseLabel, value: false },
      ],
    },
  }
}

export const truncatedTooltip = (value, length = 25, placement = 'top') => {
  if (value?.length > length) {
    return (
      <InteractiveTooltip interactive content={value} placement={placement}>
        <span>{truncate(value, { length })}</span>
      </InteractiveTooltip>
    )
  }

  return value
}

export const getFilterDisplayValues = (
  filters,
  organizationOptions = [],
  includeDate = false,
  includeDuration = false
) => {
  return {
    ...(includeDate && {
      dateRange: { label: 'Date', format: () => showDate(filters), isClearable: false },
    }),
    ...(!isEmpty(organizationOptions) && {
      organizationId: {
        label: 'Organization',
        format: (accessor) => showOrganizationResult(filters[accessor], organizationOptions),
      },
    }),

    agents: {
      label: 'Agents',
      format: (accessor, showAll) => showResult(filters[accessor], null, showAll),
    },
    tags: {
      label: 'Tags',
      format: (accessor, showAll) => showResult(filters[accessor], null, showAll),
    },

    ...((filters.channel === 'call' || filters.channel === undefined) && {
      playbooks: {
        label: 'Playbooks',
        format: (accessor, showAll) => showResult(filters[accessor], null, showAll),
      },
      ...(includeDuration
        ? {
            duration: {
              label: 'Call Duration',
              format: () => formatDurationPill(filters.callDuration, filters.maxCallDuration),
              isClearable: false,
            },
          }
        : {
            callDuration: {
              label: 'Min. Call Duration',
              format: (accessor) => `${pluralize('minute', toNumber(filters[accessor]), true)}`,
            },
            maxCallDuration: {
              label: 'Max. Call Duration',
              format: (accessor) => `${pluralize('minute', toNumber(filters[accessor]), true)}`,
            },
          }),
      voipCampaigns: {
        label: 'VOIP Campaigns',
        format: (accessor, showAll) => showResult(filters[accessor], null, showAll),
      },
      voipCustomerIds: {
        label: 'Customer ID',
        format: (accessor, showAll) => showResult(filters[accessor], null, showAll),
      },
      isWin: {
        label: 'Win',
        format: (accessor) =>
          filters[accessor] === 'true' || filters[accessor] === true
            ? 'Only Wins'
            : 'Only Non-wins',
      },
      dispositions: {
        label: 'Dispositions',
        format: (accessor, showAll) => {
          if (isArray(filters[accessor])) {
            return showResult(filters[accessor], null, showAll)
          }
          return showFirstResultDispositions(
            filters[accessor]?.selected,
            '0 selected',
            'dispositions'
          )
        },
      },
      checklist: {
        label: 'Checklist',
        format: (accessor) => showNestedResult(filters[accessor]?.selected),
      },
      deck: {
        label: 'Dynamic Prompt',
        format: (accessor) => showNestedResult(filters[accessor]?.selected),
      },
      postcall: {
        label: 'Post Call',
        format: (accessor) => showNestedResult(filters[accessor]?.selected),
      },
      notifications: {
        label: 'Notifications',
        format: (accessor) => showNestedResult(filters[accessor]?.selected),
      },
      agentType: {
        label: 'Agent Type',
        format: (accessor) => filters[accessor],
      },
      attachments: {
        label: 'Attachments',
        format: (accessor, showAll) => showResult(filters[accessor], null, showAll),
      },
      endingSentiment: {
        label: 'Ending Sentiment',
        format: (accessor, showAll) => showResult(filters[accessor], null, showAll),
      },
      direction: {
        label: 'Call Direction',
        format: (accessor) => capitalize(filters[accessor]),
      },
    }),

    includeManagers: {
      label: 'Include Managers',
      format: (accessor) => (filters[accessor] ? 'Include' : 'Exclude'),
    },
    keywords: {
      label: 'Keywords',
      format: (accessor, showAll) => showResult(filters[accessor], null, showAll),
    },
    scorecards: {
      label: 'Scorecards',
      format: (accessor, showAll) => showResult(filters[accessor], null, showAll),
    },
    scoredStatus: {
      label: 'Scored Status',
      format: (accessor) => {
        if (filters[accessor] === 'scored') return 'Scored'
        if (filters[accessor] === 'unscored') return 'Not Scored'
        if (filters[accessor] === 'partially_scored') return 'Partially Scored'
        return 'Any'
      },
    },
    scorecardThreshold: {
      label: 'Scorecard Threshold',
      format: (accessor) => {
        if (filters[accessor] === 'meets_threshold') return 'Meets Expectations'
        if (filters[accessor] === 'exceeds_threshold') return 'Exceeds Expectations'
        return 'Needs Improvement'
      },
    },

    ...(filters.channel !== 'call' && {
      channel: {
        label: 'Channel',
        format: (accessor) => startCase(filters[accessor]),
      },
      minMessageCount: {
        label: 'Min Message Count',
        format: (accessor) => filters[accessor],
      },
      maxMessageCount: {
        label: 'Max Message Count',
        format: (accessor) => filters[accessor],
      },
    }),
  }
}

export const filterValueHasChanged = (
  initialState,
  filters,
  accessor,
  alwaysVisibleFilters = []
) => {
  const initialValue = initialState.filters[accessor]
  const currentValue = filters[accessor]

  if (alwaysVisibleFilters.includes(accessor)) {
    return true
  }

  // Do not show Channel filter since we have tab that clearly shows the "filter"
  if (accessor === 'channel') {
    return false
  }

  if (accessor === 'dateRange') {
    return (
      filters.startDate !== initialState.filters.startDate ||
      filters.endDate !== initialState.filters.endDate
    )
  }

  if (accessor === 'duration') {
    return (
      filters.callDuration !== initialState.filters.callDuration ||
      filters.maxCallDuration !== initialState.filters.maxCallDuration
    )
  }

  if (accessor === 'dispositions') {
    if (isArray(filters.dispositions)) {
      return !isEmpty(filters[accessor])
    }

    const selected = filters.dispositions?.selected || []
    return selected.length > 0
  }

  // isWin is an array with 3 states, and callDuration has a non-empty default value
  if (accessor === 'isWin' || accessor === 'callDuration') {
    return currentValue !== initialValue
  }

  if (isString(initialValue) || isArray(initialValue)) {
    return !isEmpty(filters[accessor])
  }

  // Nested filters have are in the `selected` property
  if (['checklist', 'deck', 'postcall', 'notifications'].includes(accessor)) {
    return !isEmpty(filters[accessor]?.selected)
  }

  if (accessor === 'voipCampaigns') {
    return !isEmpty(filters[accessor])
  }

  if (accessor === 'scoredStatus') {
    return !!filters[accessor] && currentValue !== initialValue && currentValue !== 'any'
  }

  return currentValue !== initialValue
}

export const getScoreRange = (points = []) => {
  let min = 9
  let max = 1

  for (const [, score] of points) {
    if (score < min) min = score
    if (score > max) max = score
  }

  return { min, max }
}

export const getClosestTranscriptBlock = (currentTime, transcriptBlocks) => {
  // if this is slow for long calls we could implement a recursive binary search. Only risk is while it may be faster
  // stack size could grow large which would be problematic for people on potato comps. We'll have to see which problem is more problematic
  // speed or stack size
  let closest = 0
  for (let i = 0; i < transcriptBlocks.length; i++) {
    const currentLoopFirstTime = transcriptBlocks[i]
    if (currentLoopFirstTime <= currentTime) {
      closest = i
    } else if (currentLoopFirstTime > currentTime) {
      break
    }
  }
  return closest
}

export const getSentimentDisplay = (average) => {
  for (const [sentiment, [low, high]] of Object.entries(ENDING_SENTIMENT_RANGES)) {
    if (average >= low && average < high) {
      return startCase(sentiment)
    }
  }
  return null
}

export const getArrayNormalized = (value = [], fn) => {
  // Put single value into an array
  if (isString(value)) {
    if (fn && isFunction(fn)) {
      return [fn(value)]
    }

    return [value]
  }

  if (isArray(value)) {
    // Map array values if function is provided
    if (fn && isFunction(fn)) {
      return value.map((item) => fn(item))
    }

    // Return array as is
    return value
  }

  return []
}

export const isCamelCase = (str) => {
  // Checks for:
  // 1. Starts with a lowercase letter ([a-z])
  // 2. Can be followed by any number of letters or digits ([a-zA-Z0-9]*)
  // 3. Allows for subsequent words starting with an uppercase letter followed by lowercase letters (([A-Z][a-z]+)*)
  // 4. Does not contain underscores, hyphens, or spaces.
  return /^[a-z]+([a-zA-Z0-9]*[A-Z][a-zA-Z0-9]*)*$/.test(str) && !/[_-\s]/.test(str)
}

export const camelCaseToString = (str) => {
  return str
    .replace(/([a-z])([A-Z])/g, '$1 $2') // Add space before uppercase letters
    .replace(/([A-Z])([A-Z][a-z])/g, '$1 $2') // Add space between consecutive uppercase letters
}

export const toTitleCase = (str) => {
  return str
    .split(' ')
    .map((word) => capitalize(word))
    .join(' ')
}

const OMNICHANNEL_TYPES = [CHANNEL_TYPE.EMAIL, CHANNEL_TYPE.SMS, CHANNEL_TYPE.CHAT]

export const hasChannelAccess = (activeChannels = [], channelType, isBaltoAdmin = false) => {
  if (channelType === CHANNEL_TYPE.CALL) {
    return true
  }

  if (isBaltoAdmin && OMNICHANNEL_TYPES.includes(channelType)) {
    return true
  }

  return activeChannels.includes(channelType)
}

export const hasAnyOmnichannelAccess = (activeChannels = [], isBaltoAdmin = false) => {
  return isBaltoAdmin || !isEmpty(activeChannels)
}

export const shouldShowOmniPromotion = (activeChannels, channel, isBaltoAdmin = false) => {
  return !hasChannelAccess(activeChannels, channel, isBaltoAdmin)
}

/**
 * Returns filtered channel options based on organization's active channels.
 * Call is always included. Other channels are filtered by activeChannels.
 */
export const getChannelTypeOptions = (activeChannels = [], isBaltoAdmin = false) => {
  return omnichannelTypeOptions.filter((option) => {
    if (option.value === CHANNEL_TYPE.CALL) {
      return true
    }

    if (isBaltoAdmin && OMNICHANNEL_TYPES.includes(option.value)) {
      return true
    }

    return activeChannels.includes(option.value)
  })
}

export const formatAiCall = (filters) => {
  if (filters.agentType === 'Human Agent') {
    return false
  }

  if (filters.agentType === 'AI Agent') {
    return true
  }

  return undefined
}
